import { IBillingAdapter } from './IBillingAdapter';
import { ApiResponse } from '../../types/api';
import {
  WorkOrderCost,
  Quotation,
  Bill,
  CreateCostDto,
  CreateQuotationDto,
  CreateBillDto,
  CostFilter,
  QuotationFilter,
  BillFilter,
} from '../../types/billing';
import { ApiClient as apiClient } from '../apiClient';

export class LaravelBillingAdapter implements IBillingAdapter {
  async getCostByWorkOrder(workOrderId: string): Promise<ApiResponse<WorkOrderCost | null>> {
    return apiClient.get<WorkOrderCost | null>(`/api/v1/workorders/${workOrderId}/cost`);
  }

  async createOrUpdateCost(data: CreateCostDto): Promise<ApiResponse<WorkOrderCost>> {
    return apiClient.post<WorkOrderCost>(`/api/v1/workorders/${data.work_order_id}/cost`, data);
  }

  async deleteCostLineItem(costId: string, itemId: string): Promise<ApiResponse<WorkOrderCost>> {
    return apiClient.request<WorkOrderCost>(`/api/v1/cost/items/${itemId}`, { method: 'DELETE' });
  }

  async getQuotations(filter?: QuotationFilter): Promise<ApiResponse<Quotation[]>> {
    const params = new URLSearchParams();
    if (filter?.work_order_id) params.set('work_order_id', filter.work_order_id);
    if (filter?.vendor_id) params.set('vendor_id', filter.vendor_id);
    if (filter?.status) params.set('status', filter.status);
    return apiClient.get<Quotation[]>(`/api/v1/quotations?${params.toString()}`);
  }

  async getQuotationById(id: string): Promise<ApiResponse<Quotation>> {
    return apiClient.get<Quotation>(`/api/v1/quotations/${id}`);
  }

  async submitQuotation(data: CreateQuotationDto): Promise<ApiResponse<Quotation>> {
    return apiClient.post<Quotation>('/api/v1/quotations', data);
  }

  async approveQuotation(id: string, approvedBy: string): Promise<ApiResponse<Quotation>> {
    return apiClient.put<Quotation>(`/api/v1/quotations/${id}/approve`, { approved_by: approvedBy });
  }

  async rejectQuotation(id: string, reason: string): Promise<ApiResponse<Quotation>> {
    return apiClient.put<Quotation>(`/api/v1/quotations/${id}/reject`, { reason });
  }

  async getBills(filter?: BillFilter): Promise<ApiResponse<Bill[]>> {
    const params = new URLSearchParams();
    if (filter?.work_order_id) params.set('work_order_id', filter.work_order_id);
    if (filter?.vendor_id) params.set('vendor_id', filter.vendor_id);
    if (filter?.payment_status) params.set('payment_status', filter.payment_status);
    return apiClient.get<Bill[]>(`/api/v1/bills?${params.toString()}`);
  }

  async getBillById(id: string): Promise<ApiResponse<Bill>> {
    return apiClient.get<Bill>(`/api/v1/bills/${id}`);
  }

  async generateBill(data: CreateBillDto): Promise<ApiResponse<Bill>> {
    return apiClient.post<Bill>('/api/v1/bills', data);
  }

  async markBillPaid(billId: string, paymentReference: string): Promise<ApiResponse<Bill>> {
    return apiClient.put<Bill>(`/api/v1/bills/${billId}/pay`, { payment_reference: paymentReference });
  }
}
